'use client';

import { motion } from 'framer-motion';

const STEPS = [
  {
    n: 1,
    title: 'Upload your photos',
    desc: 'Drag & drop RAW or JPG — hundreds at a time. Your files upload securely and privately.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M7 16a4 4 0 01-.88-7.9A5 5 0 1115.9 6 5 5 0 0117 15.9M12 12v9m0-9l-3 3m3-3l3 3" />
    ),
  },
  {
    n: 2,
    title: 'Automatic HDR grouping',
    desc: 'We detect your bracketed exposures and group them into HDR sets automatically — no manual sorting.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M4 5a1 1 0 011-1h5v16H5a1 1 0 01-1-1V5zm10-1h5a1 1 0 011 1v6h-6V4zm0 9h6v6a1 1 0 01-1 1h-5v-7z" />
    ),
  },
  {
    n: 3,
    title: 'Expert editor assigned',
    desc: 'A specialist real-estate editor picks up your order and works to your chosen preferences.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    ),
  },
  {
    n: 4,
    title: 'Quality review',
    desc: 'Every image is checked against our standards before it reaches you. Unlimited revisions if needed.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
  },
  {
    n: 5,
    title: 'Secure delivery',
    desc: 'Download your finished images from your dashboard, track every order, and pay only once approved.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M12 15v3m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    ),
  },
];

export default function HowItWorks() {
  return (
    <section className="relative py-16 lg:py-20 bg-[#0F1419] border-y border-white/[0.06] overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[700px] h-[700px] bg-purple-600/5 rounded-full blur-[160px]" />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm font-semibold text-purple-400 mb-3 uppercase tracking-wider">One seamless platform</p>
          <h2 className="text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-4">
            More than editing — a complete workflow
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            Upload, automatic HDR grouping, order tracking, expert editing, quality review, and secure delivery —
            all in one place.
          </p>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="relative rounded-2xl border border-white/[0.08] bg-[#13151F]/70 p-6 hover:border-purple-500/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/25 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-purple-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {step.icon}
                  </svg>
                </div>
                <span className="text-xs font-bold text-gray-600">STEP {step.n}</span>
              </div>
              <h3 className="text-base font-bold text-white mb-1.5">{step.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>

              {/* connector arrow (desktop) */}
              {i < STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 z-10 text-purple-500/40">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
